import React, { useEffect, useState } from 'react';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  TableCell,
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  Paper,
  Box,
} from '@mui/material';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import {
  HEADER_HEIGHT,
  PARTS_SELECTION_CONTENTS_TOP,
  PARTS_SELECTION_FONT_SIZE,
} from '../../../../_constants/styleConst';
import RCodeRow from './RCode/RCodeRow';
import { RCodeInfo } from '../../PartSelection';
import { VALID_NUMBER_DIGITS } from '../../../../_constants/Const';
import { adjustToMaxDigits } from '../../../../_utils/utils';

// Rコードテーブル
type RCodeTableProps = {
  rCodeList: RCodeInfo[];
  isShowOptionParts: boolean;
  setRCodeList: React.Dispatch<React.SetStateAction<RCodeInfo[]>>;
  handlePartsListImage: (isometricDrawing: string, section: string) => void;
};

/** テーブルのヘッダー */
export const tableHeaders = [
  { width: '2%', minWidth: '', p: '3px', label: '', textAlign: 'center' }, // 並び替えアイコン
  { width: '1%', minWidth: '', p: '3px', label: '', textAlign: 'center' }, // 開閉アイコン
  {
    width: '4%',
    minWidth: '55px',
    p: '3px',
    label: 'LBL_R_CODE',
    textAlign: 'center',
  },
  {
    width: '11%',
    minWidth: '55px',
    p: '3px',
    label: 'LBL_R_DETAIL',
    textAlign: 'left',
  },
  { width: '2%', minWidth: '45px', p: '3px', label: '', textAlign: 'left' }, // チェックボックス
  {
    width: '12%',
    minWidth: '130px',
    p: '3px 0.5rem',
    label: 'LBL_PARTS_CODE',
    textAlign: 'left',
  },
  {
    width: '16%',
    minWidth: '210px',
    p: '0 1rem',
    label: 'LBL_PARTS_NAME',
    textAlign: 'left',
  },
  {
    width: '4%',
    minWidth: '55px',
    p: '3px',
    label: 'LBL_KEY',
    textAlign: 'center',
  },
  {
    width: '5%',
    minWidth: '70px',
    p: '3px',
    label: 'LBL_AMOUNT',
    textAlign: 'center',
  },
  {
    width: '18%',
    minWidth: '200px',
    p: '3px 1rem',
    label: 'LBL_SECTION',
    textAlign: 'left',
  },
  {
    width: '9%',
    minWidth: '80px',
    p: '3px',
    label: 'LBL_PARTS_LIST',
    textAlign: 'center',
  },
  {
    width: '16%',
    minWidth: '170px',
    p: '3px',
    label: 'LBL_ECN',
    textAlign: 'center',
  },
];

/** Rコード単位のテーブル */
const RCodeTable: React.FC<RCodeTableProps> = ({
  rCodeList,
  isShowOptionParts,
  setRCodeList,
  handlePartsListImage,
}) => {
  const { t } = useTranslation();
  // セクションの開閉状態
  const [expanded, setExpanded] = useState<boolean[]>([]);

  useEffect(() => {
    setExpanded(new Array(rCodeList.length).fill(false));
  }, [rCodeList.length]);

  /** パーツの選択状態を変更 */
  const onChangeSelection = (
    isChecked: boolean,
    rCode: string,
    listNo: string,
    inFigureNo: string,
  ) => {
    setRCodeList((prev) => {
      const newState = prev.map((rInfo) => ({
        ...rInfo,
        partsInfo: rInfo.partsInfo.map((part) => {
          if (part.listNo === listNo && part.inFigureNo === inFigureNo) {
            if (rInfo.r.code === rCode) {
              return { ...part, isSelect: isChecked };
            }
            // 他のRコードにある同じ部品(listNoとinFigureNo)はグレーアウトする
            return { ...part, isDuplicate: isChecked, isSelect: false };
          }
          return part;
        }),
      }));
      return newState;
    });
  };

  /** 部品コードを変更 */
  const onChangePartsCode = (
    newPartsCode: string,
    rCode: string,
    listNo: string,
    inFigureNo: string,
  ) => {
    setRCodeList((prev) => {
      const newState = prev.map((rInfo) => ({
        ...rInfo,
        partsInfo: rInfo.partsInfo.map((part) => {
          if (
            rInfo.r.code === rCode &&
            part.listNo === listNo &&
            part.inFigureNo === inFigureNo
          ) {
            return { ...part, selectedPartsCode: newPartsCode };
          }
          return part;
        }),
      }));
      return newState;
    });
  };

  /** 数量を変更 */
  const onChangeAmount = (
    newAmount: string,
    rCode: string,
    listNo: string,
    inFigureNo: string,
  ) => {
    setRCodeList((prev) => {
      const newState = prev.map((rInfo) => ({
        ...rInfo,
        partsInfo: rInfo.partsInfo.map((part) => {
          if (
            rInfo.r.code === rCode &&
            part.listNo === listNo &&
            part.inFigureNo === inFigureNo &&
            !isNaN(Number(newAmount))
          ) {
            return {
              ...part,
              amount: adjustToMaxDigits(
                Math.floor(Number(newAmount)),
                VALID_NUMBER_DIGITS.THREE,
              ),
            };
          }
          return part;
        }),
      }));
      return newState;
    });
  };

  /** アコーディオンの開閉状態を変更 */
  const toggleAccordion = (index: number) => {
    const newState = [...expanded];
    newState[index] = !expanded[index];
    setExpanded(newState);
  };

  /** Drag&Dropで並び替えた時 */
  const handleChangeOrder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    // indexの更新
    setRCodeList((prev) =>
      arrayMove(prev, Number(active.id), Number(over.id)).map((item, i) => ({
        ...item,
        index: prev[i].index,
      })),
    );
    // アコーディオンの開閉状態の更新
    setExpanded((prev) => arrayMove(prev, Number(active.id), Number(over.id)));
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        style={{
          backgroundColor: 'transparent',
        }}
        sx={{
          height: `calc(100dvh - (${HEADER_HEIGHT} + ${PARTS_SELECTION_CONTENTS_TOP} + 74px))`,
          overflow: 'auto',
        }}
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleChangeOrder}
        >
          <SortableContext
            items={rCodeList.map((rInfo) => rInfo.index)}
            strategy={verticalListSortingStrategy}
          >
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  {/* ヘッダー */}
                  {tableHeaders.map(
                    ({ width, minWidth, p, textAlign, label }, i) => (
                      <TableCell
                        key={`parts_selection_header_${i}`}
                        sx={{
                          fontSize: PARTS_SELECTION_FONT_SIZE,
                          width,
                          minWidth,
                          p,
                          py: '5px',
                          textAlign,
                        }}
                      >
                        {t(label)}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              {rCodeList.length > 0 && (
                <TableBody>
                  {rCodeList.map((rInfo, rIndex) => (
                    <RCodeRow
                      key={`parts_selection_r_code_${rIndex}`}
                      rIndex={rIndex}
                      rCode={rInfo}
                      isShowOptionParts={isShowOptionParts}
                      isExpanded={expanded[rIndex]}
                      toggleAccordion={toggleAccordion}
                      handlePartsListImage={handlePartsListImage}
                      onChangeSelection={onChangeSelection}
                      onChangePartsCode={onChangePartsCode}
                      onChangeAmount={onChangeAmount}
                    />
                  ))}
                </TableBody>
              )}
            </Table>
          </SortableContext>
        </DndContext>
      </TableContainer>
    </Box>
  );
};

export default RCodeTable;
